import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTooltip, { TooltipSection } from "../cards/subcomponents/CustomTooltip";

export type MetricTone = "neutral" | "positive" | "negative" | "auto";

const VALUE_SIZE = {
  hero: { xs: "2.75rem", sm: "3.25rem", lg: "4.125rem" },
  xl: { xs: "1.75rem", sm: "2rem", lg: "2.25rem" },
  lg: { xs: "1.5rem", sm: "1.625rem", lg: "1.875rem" },
  md: { xs: "1.25rem", lg: "1.375rem" },
  sm: { xs: "0.9375rem", lg: "1rem" },
} as const;

const SUB_SIZE = {
  hero: { xs: "0.9375rem", lg: "1rem" },
  xl: { xs: "0.875rem", lg: "0.9375rem" },
  lg: { xs: "0.8125rem", lg: "0.875rem" },
  md: { xs: "0.8125rem", lg: "0.8125rem" },
  sm: { xs: "0.75rem", lg: "0.75rem" },
} as const;

export interface MetricProps {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  /** Color for value + sub. `auto` derives it from `signal`. */
  tone?: MetricTone;
  /** Numeric sign driving `tone="auto"`. */
  signal?: number;
  size?: keyof typeof VALUE_SIZE;
  icon?: React.ElementType;
  align?: "left" | "center" | "right";
  tooltipSections?: TooltipSection[];
  /** Places sub next to the value instead of below it. */
  inlineSub?: boolean;
  /** White → gold gradient on the value (hero figures). */
  gradient?: boolean;
}

/**
 * Label → value → sub. The label stays quiet (sentence case, muted), the
 * value carries the weight.
 */
export const Metric: React.FC<MetricProps> = ({
  label,
  value,
  sub,
  tone = "neutral",
  signal,
  size = "md",
  icon: Icon,
  align = "left",
  tooltipSections,
  inlineSub = false,
  gradient = false,
}) => {
  const theme = useTheme();
  const resolvedTone =
    tone === "auto" ? ((signal ?? 0) < 0 ? "negative" : (signal ?? 0) > 0 ? "positive" : "neutral") : tone;
  const color =
    resolvedTone === "positive"
      ? theme.palette.success.main
      : resolvedTone === "negative"
      ? theme.palette.error.main
      : theme.palette.text.primary;
  const subColor = resolvedTone === "neutral" ? theme.palette.text.secondary : color;
  const justify = align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";

  const labelRow = (
    <Stack direction="row" alignItems="center" justifyContent={justify} sx={{ gap: 0.75, minWidth: 0, maxWidth: "100%" }}>
      {Icon && (
        <Icon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0, display: { xs: "none", sm: "block" } }} />
      )}
      <Typography
        component="span"
        noWrap
        sx={{ fontSize: { xs: "0.8125rem", md: "0.84375rem" }, fontWeight: 500, color: "text.secondary", lineHeight: 1.3, minWidth: 0 }}
      >
        {label}
      </Typography>
      {tooltipSections && (
        <InfoOutlinedIcon
          sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0, display: { xs: "none", sm: "block" } }}
        />
      )}
    </Stack>
  );

  const content = (
    <Stack
      spacing={size === "hero" ? 1.25 : 0.75}
      alignItems={justify}
      sx={{ textAlign: align, minWidth: 0, "& > *": { maxWidth: "100%" } }}
    >
      {labelRow}
      <Box
        sx={{
          display: "flex",
          flexDirection: inlineSub ? "row" : "column",
          alignItems: inlineSub ? "baseline" : justify,
          columnGap: 1.25,
          rowGap: 0.25,
          flexWrap: "wrap",
          justifyContent: justify,
          minWidth: 0,
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: VALUE_SIZE[size],
            fontWeight: 700,
            lineHeight: size === "hero" ? 1.02 : 1.1,
            letterSpacing: size === "hero" ? "-0.045em" : "-0.025em",
            color,
            fontVariantNumeric: "lining-nums proportional-nums",
            whiteSpace: "nowrap",
            ...(gradient && {
              background: "linear-gradient(180deg, #FFFFFF 30%, #F1D3AF 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }),
          }}
        >
          {value}
        </Typography>
        {sub !== undefined && sub !== null && (
          <Typography
            component="div"
            sx={{
              fontSize: SUB_SIZE[size],
              fontWeight: 600,
              lineHeight: 1.25,
              color: subColor,
              whiteSpace: "nowrap",
            }}
          >
            {sub}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  if (!tooltipSections) return content;

  return (
    <CustomTooltip placement="bottom" accentColor="secondary.main" sections={tooltipSections}>
      <Box sx={{ cursor: "help" }}>{content}</Box>
    </CustomTooltip>
  );
};

export default Metric;
